'use client';

import { Hide, HStack, Show, SimpleGrid } from '@chakra-ui/react';
import { HomeCard } from '../cards/HomeCard';
import { getHomePath } from '@habity/services';
import { useEffect, useState } from 'react';
import { NestedHome, NestedUser } from '@habity/types';
import { useUser } from '~/hooks/useUser';
import { getAPI } from '~/lib/api';
import { API_PATHS } from '@habity/constants';
import { getQueryParamsString } from '~/lib/providers/neondatabase';
import { STYLES } from '~/components/utility/utils';

const MAX_GRID_LENGTH = 6;
const DEBUG_GRID_LENGTH = 30;

interface CuratedHomesGridProps {
  homeIds: string; // Comma-separated home ids
  desktopLayout?: 'grid' | 'scroll';
  debug?: boolean;
}

export function CuratedHomesGrid({
  homeIds,
  desktopLayout = 'grid',
  debug = false,
}: CuratedHomesGridProps) {
  const [homes, setHomes] = useState<NestedHome[]>([]);
  const { user } = useUser();

  const limit = debug ? DEBUG_GRID_LENGTH : MAX_GRID_LENGTH;

  const searchParams = new URLSearchParams();

  if (debug) {
    // Include sold homes. This helps with identifying which ones need to be cleaned up
    searchParams.append('statuses', 'soon_for_sale,for_sale,sold');
  }

  const fetchHomes = async () => {
    const queryParams = getQueryParamsString({
      page: '0',
      limit: Math.min(homeIds.split(',').length, limit),
      searchParams,
      entityIds: homeIds,
    });
    const _homes: NestedHome[] = await getAPI(
      API_PATHS.PROVIDERS.NEONDATABASE.HOMES.INDEX,
      queryParams
    );

    setHomes(_homes);
  };

  useEffect(() => {
    fetchHomes();
  }, [homeIds, debug]);

  return (
    <>
      {/* Desktop layout is chosen from props */}
      <Hide below="desktop">
        {desktopLayout === 'grid' ? (
          <SimpleGrid
            w="full"
            minChildWidth="380px"
            rowGap="10"
            columnGap="5"
            pb={{ mobile: '12', desktop: '24' }}
            pt="0"
          >
            {homes.map((home: NestedHome) => (
              <HomeCard
                key={home.id}
                home={home}
                user={user ?? undefined}
                href={getHomePath(home)}
                actions={false}
                onClick={() => null}
                isLoading={home?.loading || false}
                variant="display"
                aspectRatio={1 / 1.27}
                debug={debug}
              />
            ))}
          </SimpleGrid>
        ) : (
          <HomesListSideScroll homes={homes} user={user} />
        )}
      </Hide>

      {/* Mobile layout is always side-scroll */}
      <Show below="desktop">
        <HomesListSideScroll homes={homes} user={user} />
      </Show>
    </>
  );
}

interface HomesListSideScrollProps {
  homes: NestedHome[];
  user: NestedUser | null;
}

function HomesListSideScroll({ homes, user }: HomesListSideScrollProps) {
  return (
    <HStack
      w="full"
      overflowX="scroll"
      sx={{
        ...STYLES.hideScrollbar,
      }}
    >
      {homes.map((home: NestedHome) => (
        <HomeCard
          key={home.id}
          home={home}
          user={user ?? undefined}
          href={getHomePath(home)}
          actions={false}
          onClick={() => null}
          minW="400px"
          aspectRatio={1 / 1.27}
          isLoading={home?.loading || false}
          variant="display"
          showBottomBorder={false}
        />
      ))}
    </HStack>
  );
}
